import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { AutoAction, IntegrityCase, IntegritySavingsStats, MatchHit, MatchRule, MatchRuleCode, Severity } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ASP_INTEGRITY_OFFICER', 'ASP_CEO'];
const READER_ROLES = ['ASP_INTEGRITY_OFFICER', 'ASP_CEO', 'OAG_AUDITOR'];

/**
 * Staff-only investigative screen — never citizen-facing. S07's rules,
 * hits and stats endpoints are genuine lists; cases have no `GET` list, so
 * cases opened from this browser are cached locally (the real POST
 * /hits/:id/open-case response) so they can be closed later.
 */
export function Integrity() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const canRead = persona?.roles.some((r) => READER_ROLES.includes(r)) ?? false;

  const [statusFilter, setStatusFilter] = useState('');
  const [ruleFilter, setRuleFilter] = useState('');
  const [showNewRule, setShowNewRule] = useState(false);
  const [showNightlyRun, setShowNightlyRun] = useState(false);
  const [cases, setCases] = useState<IntegrityCase[]>(() => loadList<IntegrityCase>('integrity.cases'));
  const [closeTarget, setCloseTarget] = useState<IntegrityCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [investigatorId, setInvestigatorId] = useState('');
  const [openHitId, setOpenHitId] = useState<string | null>(null);

  const savingsQ = useApiQuery<IntegritySavingsStats>(canRead ? '/api/v1/integrity/stats/savings' : null);
  const rulesQ = useApiQuery<{ items: MatchRule[] }>(canRead ? '/api/v1/integrity/rules' : null);
  const hitsQ = useApiQuery<{ items: MatchHit[] }>(
    canRead ? `/api/v1/integrity/hits?${new URLSearchParams({ ...(statusFilter ? { status: statusFilter } : {}), ...(ruleFilter ? { rule: ruleFilter } : {}) })}` : null,
  );

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Integrity" />
        <ErrorNote message="This view requires the Integrity Officer, ASP Chief Executive, or OAG Auditor role." />
      </div>
    );
  }

  const act = async (hitId: string, action: 'confirm' | 'false-positive') => {
    setBusyId(hitId);
    setError(null);
    try {
      await api.post(`/api/v1/integrity/hits/${hitId}/${action}`);
      hitsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const openCase = async (hitId: string) => {
    if (!investigatorId) return;
    setBusyId(hitId);
    setError(null);
    try {
      const created = await api.post<IntegrityCase>(`/api/v1/integrity/hits/${hitId}/open-case`, { investigatorId });
      setCases(upsertList('integrity.cases', created));
      setOpenHitId(null);
      hitsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open case');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Integrity: Ghost-Beneficiary & Data Matching"
        subtitle="Continuous matching, not annual embarrassment"
        actions={
          isOfficer ? (
            <>
              <button className="btn-secondary" onClick={() => setShowNewRule(true)}>
                + Rule
              </button>
              <button className="btn-primary" onClick={() => setShowNightlyRun(true)}>
                Run matching now
              </button>
            </>
          ) : undefined
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {savingsQ.loading ? (
          <Spinner />
        ) : (
          <>
            <StatCard label="Confirmed cases" value={savingsQ.data?.confirmedCases ?? 0} tone="warn" />
            <StatCard label="Recovered" value={scr(savingsQ.data?.recoveredSCR ?? 0)} tone="good" />
            <StatCard label="Prevented / month" value={scr(savingsQ.data?.preventedMonthlySCR ?? 0)} tone="good" />
            <StatCard label="Prevented / year" value={scr(savingsQ.data?.preventedAnnualisedSCR ?? 0)} tone="good" hint="the Minister's number" />
          </>
        )}
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Match rules</h2>
        <div className="card overflow-x-auto">
          {rulesQ.loading ? (
            <Spinner />
          ) : (rulesQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No rules configured" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Code</th>
                  <th className="table-head">Description</th>
                  <th className="table-head">Severity</th>
                  <th className="table-head">Auto action</th>
                  <th className="table-head">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {rulesQ.data!.items.map((r) => (
                  <tr key={r.id}>
                    <td className="table-cell text-xs font-medium">{r.code}</td>
                    <td className="table-cell text-xs">{r.description}</td>
                    <td className="table-cell text-xs">{r.severity}</td>
                    <td className="table-cell text-xs">{r.autoAction}</td>
                    <td className="table-cell text-xs">{r.enabled ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-bold text-slate-700">Hits — triage queue</h2>
          <div className="flex gap-2">
            <select className="field-input max-w-[160px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="OPEN">Open</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="FALSE_POSITIVE">False positive</option>
            </select>
            <select className="field-input max-w-[180px]" value={ruleFilter} onChange={(e) => setRuleFilter(e.target.value)}>
              <option value="">All rules</option>
              {(rulesQ.data?.items ?? []).map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="card overflow-x-auto">
          {hitsQ.loading ? (
            <Spinner />
          ) : (hitsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No hits" hint="The nightly matching run populates this queue." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">NIN</th>
                  <th className="table-head">Rule</th>
                  <th className="table-head">Evidence</th>
                  <th className="table-head">Status</th>
                  {isOfficer ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {hitsQ.data!.items.map((h) => (
                  <tr key={h.id}>
                    <td className="table-cell text-xs">{h.nin}</td>
                    <td className="table-cell text-xs">{h.ruleCode}</td>
                    <td className="table-cell text-xs text-slate-500">{JSON.stringify(h.evidence)}</td>
                    <td className="table-cell">
                      <StatusBadge status={h.status} />
                    </td>
                    {isOfficer ? (
                      <td className="table-cell text-right">
                        {h.status === 'OPEN' ? (
                          <div className="flex justify-end gap-1.5">
                            <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => act(h.id, 'false-positive')} disabled={busyId === h.id}>
                              False positive
                            </button>
                            <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => act(h.id, 'confirm')} disabled={busyId === h.id}>
                              Confirm
                            </button>
                          </div>
                        ) : h.status === 'CONFIRMED' && !h.caseId ? (
                          openHitId === h.id ? (
                            <div className="flex justify-end gap-1.5">
                              <input className="field-input w-28 !py-1 text-xs" placeholder="Investigator" value={investigatorId} onChange={(e) => setInvestigatorId(e.target.value)} />
                              <button className="btn-primary !px-2 !py-0.5 text-xs" onClick={() => openCase(h.id)} disabled={busyId === h.id || !investigatorId}>
                                Open case
                              </button>
                            </div>
                          ) : (
                            <button className="btn-primary !px-2 !py-0.5 text-xs" onClick={() => setOpenHitId(h.id)}>
                              Open case
                            </button>
                          )
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isOfficer ? (
        <div>
          <h2 className="mb-2 text-sm font-bold text-slate-700">Cases opened this session</h2>
          <div className="card overflow-x-auto">
            {cases.length === 0 ? (
              <EmptyState title="No cases opened yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Reference</th>
                    <th className="table-head">NIN</th>
                    <th className="table-head">Investigator</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) => (
                    <tr key={c.id}>
                      <td className="table-cell font-medium">{c.refNumber}</td>
                      <td className="table-cell text-xs">{c.nin}</td>
                      <td className="table-cell text-xs">{c.investigatorId}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="table-cell text-right">
                        {c.status === 'OPEN' ? (
                          <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setCloseTarget(c)}>
                            Close
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showNewRule ? (
        <NewRuleModal
          onClose={() => setShowNewRule(false)}
          onDone={() => {
            setShowNewRule(false);
            rulesQ.reload();
          }}
        />
      ) : null}
      {showNightlyRun ? (
        <NightlyRunModal
          onClose={() => setShowNightlyRun(false)}
          onDone={() => {
            setShowNightlyRun(false);
            hitsQ.reload();
          }}
        />
      ) : null}
      {closeTarget ? (
        <CloseCaseModal
          integrityCase={closeTarget}
          onClose={() => setCloseTarget(null)}
          onDone={(updated) => {
            setCases(upsertList('integrity.cases', updated));
            setCloseTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function NewRuleModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState<MatchRuleCode>('EMPLOYER_REMITTANCE_GAP');
  const [description, setDescription] = useState('');
  const [sources, setSources] = useState('');
  const [severity, setSeverity] = useState<Severity>('MEDIUM');
  const [autoAction, setAutoAction] = useState<AutoAction>('FLAG');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/integrity/rules', {
        code,
        description,
        sources: sources.split(',').map((s) => s.trim()).filter(Boolean),
        severity,
        autoAction,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create rule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New match rule" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Code">
          <select className="field-input" value={code} onChange={(e) => setCode(e.target.value as MatchRuleCode)}>
            <option value="DEATH_MATCH">DEATH_MATCH</option>
            <option value="ABROAD_90D">ABROAD_90D</option>
            <option value="INCOME_UNDECLARED">INCOME_UNDECLARED</option>
            <option value="DOUBLE_DIP">DOUBLE_DIP</option>
            <option value="EMPLOYER_REMITTANCE_GAP">EMPLOYER_REMITTANCE_GAP</option>
            <option value="ASSET_SIGNAL">ASSET_SIGNAL</option>
          </select>
        </Field>
        <Field label="Description">
          <input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Sources (comma-separated feeds)">
          <input className="field-input" value={sources} onChange={(e) => setSources(e.target.value)} placeholder="e.g. SRC, SPF" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Severity">
            <select className="field-input" value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
            </select>
          </Field>
          <Field label="Auto action">
            <select className="field-input" value={autoAction} onChange={(e) => setAutoAction(e.target.value as AutoAction)}>
              <option value="FLAG">Flag only</option>
              <option value="SUSPEND">Auto-suspend</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !description || !sources}>
            {busy ? 'Saving…' : 'Create rule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NightlyRunModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [runId, setRunId] = useState(() => `RUN-${new Date().toISOString().slice(0, 10)}`);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [incomeThreshold, setIncomeThreshold] = useState(10000);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ hits: number; byRule: Record<string, number>; autoSuspended: number } | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ hits: number; byRule: Record<string, number>; autoSuspended: number }>('/api/v1/integrity/runs/nightly', {
        runId,
        asOf,
        incomeThreshold,
      });
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Run continuous matching" onClose={result ? onDone : onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {result ? (
          <p className="text-sm text-slate-700">
            {result.hits} new hit(s) — {Object.entries(result.byRule).map(([k, v]) => `${k}: ${v}`).join(', ') || 'none'}. {result.autoSuspended} beneficiary(ies)
            auto-suspended pending triage.
          </p>
        ) : (
          <>
            <Field label="Run id (period-stamped)">
              <input className="field-input" value={runId} onChange={(e) => setRunId(e.target.value)} />
            </Field>
            <Field label="As of date">
              <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </Field>
            <Field label="Undeclared-income threshold (SCR/month)">
              <input className="field-input" type="number" value={incomeThreshold} onChange={(e) => setIncomeThreshold(Number.parseFloat(e.target.value))} />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={result ? onDone : onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result ? (
            <button className="btn-primary" onClick={submit} disabled={busy || !runId}>
              {busy ? 'Running…' : 'Run'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function CloseCaseModal({ integrityCase, onClose, onDone }: { integrityCase: IntegrityCase; onClose: () => void; onDone: (c: IntegrityCase) => void }) {
  const [outcome, setOutcome] = useState<'CONFIRMED' | 'CLEARED'>('CONFIRMED');
  const [note, setNote] = useState('');
  const [recoveredAmount, setRecoveredAmount] = useState('0');
  const [preventedMonthly, setPreventedMonthly] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<IntegrityCase>(`/api/v1/integrity/cases/${integrityCase.id}/close`, {
        outcome,
        note,
        recoveredAmount: Number.parseFloat(recoveredAmount) || 0,
        preventedMonthly: Number.parseFloat(preventedMonthly) || 0,
      });
      onDone({ ...integrityCase, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not close case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Close case ${integrityCase.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
            <option value="CONFIRMED">Confirmed — fraud/ineligibility established</option>
            <option value="CLEARED">Cleared — beneficiary vindicated</option>
          </select>
        </Field>
        {outcome === 'CONFIRMED' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Recovered (SCR)">
              <input className="field-input" type="number" value={recoveredAmount} onChange={(e) => setRecoveredAmount(e.target.value)} />
            </Field>
            <Field label="Prevented / month (SCR)">
              <input className="field-input" type="number" value={preventedMonthly} onChange={(e) => setPreventedMonthly(e.target.value)} />
            </Field>
          </div>
        ) : null}
        <Field label="Note">
          <textarea className="field-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || note.length < 3}>
            {busy ? 'Closing…' : 'Close case'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
